/**
 * Patient match words for intake (Samer, 20 Oct demo): "This needs to tell me whether this is confirmed or not. If the
 * patient right or wrong." A nurse or an admin reads three states only, never the API's words:
 *
 * - Matches: name and date of birth match this patient (`Verified`), or someone confirmed the patient
 *   (`patientConfirmed`). Green.
 * - Check details: some details match or could not be read (`NeedsIdentityReview`, `NotChecked`, or a value this app
 *   does not know yet). Amber. The words say what to do.
 * - Different patient: the document names someone else (`Conflict`). Red. The words say not to file it here.
 *
 * `Conflict` wins over `patientConfirmed`: a confirmation never turns a different patient into a match.
 * The document's printed name and date of birth are never stored or returned (privacy rule), so the review shows only
 * what each check found, per identifier (`identityCheckText`). The glyph is the PM StatusGlyph kind.
 */
import type { GlyphKind } from '@/shared/ui/components/status-glyph-tokens';
import type { IntakeIdentityCheck, IntakeIdentityStatus } from '../types';

export type PatientMatchState = 'matches' | 'check' | 'different';

export interface PatientMatch {
  state: PatientMatchState;
  label: 'Matches' | 'Check details' | 'Different patient';
  semantic: 'success' | 'warning' | 'error';
  glyph: GlyphKind;
  /** One sentence: what the state means, and what to do. */
  guidance: string;
}

const MATCHES = { state: 'matches', label: 'Matches', semantic: 'success', glyph: 'done' } as const;
const CHECK = { state: 'check', label: 'Check details', semantic: 'warning', glyph: 'up' } as const;
const DIFFERENT = { state: 'different', label: 'Different patient', semantic: 'error', glyph: 'alert' } as const;

export const CHECK_GUIDANCE = 'Check the name and date of birth, then confirm.';
export const DIFFERENT_GUIDANCE = 'This document names someone else. Do not file it here.';

export function patientMatch(status: IntakeIdentityStatus, patientConfirmed: boolean | null | undefined): PatientMatch {
  if (status === 'Conflict') return { ...DIFFERENT, guidance: DIFFERENT_GUIDANCE };
  if (patientConfirmed === true) return { ...MATCHES, guidance: 'Confirmed as this patient.' };
  if (status === 'Verified') return { ...MATCHES, guidance: 'Name and date of birth match this patient.' };
  if (status === 'NeedsIdentityReview') return { ...CHECK, guidance: `Some details match. ${CHECK_GUIDANCE}` };
  if (status === 'NotChecked') return { ...CHECK, guidance: `The details could not be read. ${CHECK_GUIDANCE}` };
  // A value this app does not know yet: never green, never red.
  return { ...CHECK, guidance: CHECK_GUIDANCE };
}

/**
 * Every amber "Check details" state waits for a person to say "This is <first name>": the server's identity rule
 * (Patient `IntakeConfirmService.IdentityProblem`, contract v0.20) passes only `Verified`, never `Conflict`, and
 * everything else only once `patientConfirmed` is true (else 422 `patient_not_confirmed`).
 */
export const needsPatientConfirm = (status: IntakeIdentityStatus, patientConfirmed: boolean | null | undefined) =>
  status !== 'Verified' && status !== 'Conflict' && patientConfirmed !== true;

const IDENTIFIER_LABEL: Record<string, string> = { Name: 'Name', DateOfBirth: 'Date of birth', Mrn: 'MRN', Sex: 'Sex', Language: 'Language' };

export const identifierLabel = (identifier: string) => IDENTIFIER_LABEL[identifier] ?? identifier;

/**
 * What one check found on the document, for the "On the document" side. Not printed is neutral (it is not a
 * mismatch); a mismatch is an error.
 */
export function identityCheckText(c: IntakeIdentityCheck | undefined): { text: string; glyph: GlyphKind } {
  if (!c) return { text: 'Not checked', glyph: 'empty' };
  if (c.result === 'Match') return { text: 'Same', glyph: 'done' };
  if (c.result === 'Mismatch') return { text: 'Different', glyph: 'alert' };
  if (c.result === 'Missing') return { text: 'Not printed or not readable', glyph: 'empty' };
  // The Identify step writes Unverified when the field could not be compared (on dev: every MRN check).
  if (c.result === 'Unverified') return { text: 'Could not be checked', glyph: 'empty' };
  return { text: String(c.result), glyph: 'empty' };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A birth date is a calendar day: read from its `YYYY-MM-DD` start, never through a time zone. */
export function formatBirthDate(iso: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  if (!m) return null;
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${Number(m[3])} ${month} ${m[1]}` : null;
}
